import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

const TEMPLATE_WIZARD_PATH = /^\/create\/templates\/[^/]+\/[^/]+\/?$/;

/**
 * Warns before a browser reload/close while on the scaffolder wizard, since
 * its form state only lives in memory and a reload wipes it. Deliberately
 * doesn't try to detect whether the user actually typed anything: MUI's
 * Select/Autocomplete widgets (used for enum/picker fields, e.g. the
 * Tenant/Domain/System pickers in this package) don't dispatch real native
 * input/change DOM events, so that heuristic missed most template fields and
 * the warning silently never fired.
 */
export const UnsavedWizardGuard = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const isOnWizard = TEMPLATE_WIZARD_PATH.test(pathname);

  useEffect(() => {
    if (!isOnWizard) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () =>
      window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isOnWizard]);

  return <>{children}</>;
};
